import React from 'react';
import { FormGroup, Input, Label } from 'reactstrap';
import { Control, Controller, FieldPath, FieldValues, RegisterOptions } from 'react-hook-form';
import 'react-datepicker/dist/react-datepicker.css';

import { IOption } from 'models/Form';
import './FormComponents.css';

export interface ITransform<TSelectedValue, TFormValue> {
  toFormValue: (i: TSelectedValue) => TFormValue;
  toSelectedValue: (i: TFormValue) => TSelectedValue;
  selectedValueToString: (i: TSelectedValue) => string;
  formValueToString: (o: TFormValue) => string;
}

export interface ICustomRadioButtonsProps<T extends FieldValues, TOptionValue> {
  control: Control<T, object>;
  fieldName: FieldPath<T>;
  options: IOption<TOptionValue>[];
  defaultOption?: IOption<TOptionValue>;
  label: string;
  transform?: ITransform<IOption<TOptionValue>, TOptionValue>;
  rules?: Omit<
    RegisterOptions<T, FieldPath<T>>,
    'valueAsNumber' | 'valueAsDate' | 'setValueAs' | 'disabled'
  >;
  readonly?: boolean;
}

export function ControlledRadioButtons<T extends FieldValues, TOptionValue>({
  fieldName,
  control,
  options,
  defaultOption,
  label,
  rules,
  transform,
  readonly,
}: ICustomRadioButtonsProps<T, TOptionValue>) {
  const getOptionFromValue = (value: TOptionValue) => {
    var candidates = options.filter((o) => o.value === value);
    if (candidates.length === 1) {
      return candidates[0];
    }
    return undefined;
  };

  return (
    <Controller
      name={fieldName}
      control={control}
      rules={rules || {}}
      render={({ field, fieldState, formState }) => {
        if (!field.value && defaultOption) {
          options.forEach((o) => {
            if (o.value == defaultOption.value) {
              field.onChange(o.value);
            }
          });
        }

        const isInvalid: boolean =
          fieldState.invalid && (fieldState.isTouched || formState.isSubmitted);
        const className = isInvalid ? 'radiobutton-group is-invalid' : '';

        const onChange = (e: any) => {
          if (transform !== undefined) {
            const valueAsString = e.currentTarget.value;
            const valueAsObject = options.filter(
              (o) => transform.selectedValueToString(o) === valueAsString
            );
            field.onChange(valueAsObject.length ? valueAsObject[0].value : options[0].value);
          } else {
            field.onChange(e);
          }
        };

        if (readonly) {
          const o: IOption<TOptionValue> | undefined = getOptionFromValue(
            field.value as TOptionValue
          );
          return (
            <>
              <span>{label}</span>
              <Input readOnly value={o ? o.label : (field.value as string)} disabled={readonly} />
            </>
          );
        }

        return (
          <>
            <span>{label}</span>
            <FormGroup check className={className}>
              {options.map((o, index) => (
                <Label key={index} check>
                  <Input
                    type="radio"
                    readOnly={readonly}
                    name={field.name}
                    onChange={onChange}
                    value={
                      transform ? transform.selectedValueToString(o) : (o.value as any).toString()
                    }
                    checked={o.value === field.value}
                  />
                  {o.label}
                  <br />
                </Label>
              ))}
            </FormGroup>
          </>
        );
      }}
    />
  );
}
